/**
 * Color Utilities - Fleet Premium
 * Funcoes de manipulacao de cores para segmentos de rota
 */

/**
 * Paleta base de cores para rotas
 */
export const ROUTE_BASE_COLORS = [
  '#00d4aa', // teal
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#10b981', // emerald
  '#3b82f6', // blue
  '#ec4899', // pink
  '#6366f1', // indigo
];

/**
 * Paleta de cores para legs (mais suaves)
 */
export const LEG_COLORS = [
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#a855f7', // purple
  '#22c55e', // green
  '#f43f5e', // rose
  '#0ea5e9', // sky
  '#eab308', // yellow
  '#14b8a6', // teal
  '#d946ef', // fuchsia
];

/**
 * Converte cor hex para RGB
 * @param {string} hex - Cor hexadecimal (#RRGGBB)
 * @returns {{ r: number, g: number, b: number }} Componentes RGB
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
};

/**
 * Converte RGB para hex
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} Cor hexadecimal
 */
export const rgbToHex = (r, g, b) => {
  const toHex = (c) => {
    const hex = Math.max(0, Math.min(255, Math.round(c))).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Ajusta o brilho de uma cor
 * @param {string} hexColor - Cor hexadecimal
 * @param {number} percent - Porcentagem de ajuste (-100 a 100)
 * @returns {string} Nova cor hexadecimal
 */
export const adjustBrightness = (hexColor, percent) => {
  const { r, g, b } = hexToRgb(hexColor);
  const amount = Math.round(2.55 * percent);
  return rgbToHex(r + amount, g + amount, b + amount);
};

/**
 * Ajusta a saturacao de uma cor
 * @param {string} hexColor - Cor hexadecimal
 * @param {number} percent - Porcentagem de ajuste (-100 a 100)
 * @returns {string} Nova cor hexadecimal
 */
export const adjustSaturation = (hexColor, percent) => {
  const { r, g, b } = hexToRgb(hexColor);
  const gray = 0.2989 * r + 0.587 * g + 0.114 * b;
  const factor = 1 + (percent / 100);

  return rgbToHex(
    gray + factor * (r - gray),
    gray + factor * (g - gray),
    gray + factor * (b - gray),
  );
};

/**
 * Gera cor para um leg baseado na cor base e indice
 * @param {string} baseColor - Cor base da rota
 * @param {number} legIndex - Indice do leg (0-based)
 * @param {number} totalLegs - Total de legs na rota
 * @returns {string} Cor do leg
 */
export const generateLegColor = (baseColor, legIndex, totalLegs) => {
  if (totalLegs <= 1) return baseColor;

  // Para poucos legs, usar cores da paleta
  if (totalLegs <= LEG_COLORS.length) {
    return LEG_COLORS[legIndex % LEG_COLORS.length];
  }

  // Para muitos legs, variar a cor base
  const hueShift = (legIndex / totalLegs) * 60 - 30; // -30 a +30 graus
  const brightnessShift = ((legIndex % 3) - 1) * 15; // -15, 0, +15

  let color = adjustBrightness(baseColor, brightnessShift);
  color = adjustSaturation(color, hueShift);

  return color;
};

/**
 * Gera cor para uma rota baseado no indice
 * @param {number} routeIndex - Indice da rota (0-based)
 * @returns {string} Cor da rota
 */
export const getRouteColor = (routeIndex) => {
  return ROUTE_BASE_COLORS[routeIndex % ROUTE_BASE_COLORS.length];
};

/**
 * Gera array de cores para legs de uma rota
 * @param {string} routeColor - Cor base da rota
 * @param {number} legCount - Numero de legs
 * @returns {string[]} Array de cores para cada leg
 */
export const generateLegColors = (routeColor, legCount) => {
  if (legCount === 0) return [];
  if (legCount === 1) return [routeColor];

  return Array.from({ length: legCount }, (_, i) =>
    generateLegColor(routeColor, i, legCount),
  );
};

/**
 * Mistura duas cores
 * @param {string} color1 - Primeira cor hexadecimal
 * @param {string} color2 - Segunda cor hexadecimal
 * @param {number} ratio - Proporcao (0 = color1, 1 = color2)
 * @returns {string} Cor misturada
 */
export const mixColors = (color1, color2, ratio = 0.5) => {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  return rgbToHex(
    c1.r + (c2.r - c1.r) * ratio,
    c1.g + (c2.g - c1.g) * ratio,
    c1.b + (c2.b - c1.b) * ratio,
  );
};

/**
 * Calcula cor de contraste (preto ou branco) para texto
 * @param {string} hexColor - Cor de fundo
 * @returns {string} '#000000' ou '#ffffff'
 */
export const getContrastColor = (hexColor) => {
  const { r, g, b } = hexToRgb(hexColor);
  // Calcula luminosidade relativa
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

export default {
  ROUTE_BASE_COLORS,
  LEG_COLORS,
  hexToRgb,
  rgbToHex,
  adjustBrightness,
  adjustSaturation,
  generateLegColor,
  getRouteColor,
  generateLegColors,
  mixColors,
  getContrastColor,
};
